import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  forwardRef,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  ArrowUp,
  ArrowUpRight,
  Check,
  Circle as CircleIcon,
  MessageSquare,
  Minus,
  MousePointer2,
  Pen,
  Square as SquareIcon,
  Trash,
  Type as TypeIcon,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { exportSurfaceSnapshot } from '@/artifact/export';
import { findAgentCanvasAnnotationMetadataAtPoint } from '@/artifact/agentCanvasMetadata';
import type {
  AgentCanvasAnnotation,
  AgentCanvasChatContext,
  AgentCanvasPoint,
  AgentCanvasSubmission,
  AgentCanvasViewport,
} from '@/artifact/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { cn } from '@/lib/utils';
import { useAgentCanvasToolbarSlot } from './agentCanvasToolbarSlot';
import { PaperChatSubmitButton } from './paper/PaperChatInput';
import {
  PAPER_CHROME_RADIUS_VALUE,
  PAPER_CONTROL_RADIUS_VALUE,
  PAPER_ICON_CONTROL_COMPACT_CLASS,
  PAPER_ICON_CONTROL_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_STROKE_WIDTH,
} from './paper/paperShapes';

type AgentCanvasTool =
  | 'cursor'
  | 'stroke'
  | 'line'
  | 'arrow'
  | 'box'
  | 'circle'
  | 'text'
  | 'comment';

const ALL_TOOLS: AgentCanvasTool[] = [
  'cursor', 'stroke', 'line', 'arrow', 'box', 'circle', 'text', 'comment',
];

/** Draft state for the in-progress shape currently being drawn. */
type Draft =
  | { tool: 'stroke'; path: AgentCanvasPoint[] }
  | {
      tool: 'line' | 'arrow' | 'box' | 'circle';
      start: AgentCanvasPoint;
      end: AgentCanvasPoint;
    };

interface AgentCanvasOverlayProps {
  active: boolean;
  containerRef: RefObject<HTMLDivElement>;
  onSubmit: (submission: AgentCanvasSubmission) => boolean;
  onSubmitted?: () => void;
  /**
   * Initial tool. `null` starts the canvas in passive mode (pointer events
   * pass through to the artifact underneath); used by the paper rail
   * variant. Defaults to `'cursor'` for legacy.
   */
  initialTool?: AgentCanvasTool | null;
}

export interface AgentCanvasOverlayHandle {
  collectContext: () => Promise<AgentCanvasChatContext | null>;
  clear: () => void;
}

interface Size {
  width: number;
  height: number;
}

interface PointEditorState {
  /** Whether the editor is producing a `comment` (numbered pin) or a free-floating `text` annotation. */
  kind: 'comment' | 'text';
  pos: AgentCanvasPoint;
  value: string;
}

interface EncodedImage {
  mimeType: string;
  base64: string;
}

const PALETTE = ['#f59e0b', '#a78bfa', '#ef4444', '#22c55e', '#06b6d4', '#f8fafc'];
const LINE_WIDTH = 3;
const TEXT_FONT_SIZE = 16;
const TEXT_LINE_HEIGHT = 20;
const TEXT_FONT = `${TEXT_FONT_SIZE}px ui-sans-serif, system-ui, sans-serif`;
const TOOL_TIPS: Record<AgentCanvasTool, { title: string; description: string }> = {
  cursor: {
    title: 'Cursor',
    description: 'Use the panel normally. Click charts, buttons, tables, and links.',
  },
  stroke: {
    title: 'Draw',
    description: 'Circle or underline a region when words would be slower.',
  },
  line: {
    title: 'Line',
    description: 'Drag to draw a straight line.',
  },
  arrow: {
    title: 'Arrow',
    description: 'Drag to draw an arrow pointing at something.',
  },
  box: {
    title: 'Box',
    description: 'Drag to draw a rectangle around a region.',
  },
  circle: {
    title: 'Circle',
    description: 'Drag from center outward to draw a circle.',
  },
  text: {
    title: 'Text',
    description: 'Click to drop a free-floating text annotation.',
  },
  comment: {
    title: 'Point comment',
    description: 'Drop a numbered note on the exact thing you want the agent to inspect.',
  },
};
const PAPER_CANVAS_ICON_PROPS = {
  className: PAPER_ICON_CLASS,
  strokeWidth: PAPER_ICON_STROKE_WIDTH,
} as const;

const TOOL_ICON: Record<AgentCanvasTool, ReactNode> = {
  cursor: <MousePointer2 {...PAPER_CANVAS_ICON_PROPS} />,
  stroke: <Pen {...PAPER_CANVAS_ICON_PROPS} />,
  line: (
    <Minus
      className={cn(PAPER_ICON_CLASS, '-rotate-45')}
      strokeWidth={PAPER_ICON_STROKE_WIDTH}
    />
  ),
  arrow: <ArrowUpRight {...PAPER_CANVAS_ICON_PROPS} />,
  box: <SquareIcon {...PAPER_CANVAS_ICON_PROPS} />,
  circle: <CircleIcon {...PAPER_CANVAS_ICON_PROPS} />,
  text: <TypeIcon {...PAPER_CANVAS_ICON_PROPS} />,
  comment: <MessageSquare {...PAPER_CANVAS_ICON_PROPS} />,
};

const ToolTipBody = (props: { title: string; description: string }) => (
  <div className="max-w-48 space-y-0.5">
    <div className="font-medium text-zinc-100">{props.title}</div>
    <div className="text-[11px] leading-snug text-zinc-400">{props.description}</div>
  </div>
);

const PaperToolTipBody = (props: { title: string; description: string }) => (
  <div className="max-w-48 space-y-0.5">
    <div className="font-medium text-[var(--fg)]">{props.title}</div>
    <div className="text-[11px] leading-snug text-[var(--fg-3)]">{props.description}</div>
  </div>
);

const PAPER_TOOLTIP_CLS =
  'bg-[var(--surface)] border border-solid border-[var(--hair)] text-[var(--fg-2)] px-4 py-2 shadow-md';

const dataUrlBase64 = (dataUrl: string) => {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
};

const dataUrlMimeType = (dataUrl: string) => {
  const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return match?.[1] ?? 'application/octet-stream';
};

const nextCommentMarker = (annotations: AgentCanvasAnnotation[]) => {
  const maxMarker = annotations.reduce((max, annotation) => {
    if (annotation.kind !== 'comment') return max;
    const marker = Number.parseInt(annotation.marker ?? '', 10);
    return Number.isFinite(marker) ? Math.max(max, marker) : max;
  }, 0);
  return String(maxMarker + 1);
};

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Could not decode Agent Canvas screenshot.'));
  image.src = src;
});

/**
 * Where to anchor a note editor for a shape that was just committed.
 *
 * Strokes / lines / arrows: midpoint of the stroke or segment.
 * Mark: just below-right of the X.
 * Box / circle: just *outside* the bounded area (top-right of the box,
 * upper-right of the circle's bounding square) so the editor doesn't sit
 * on top of the shape.
 */
const shapeNoteAnchor = (annotation: AgentCanvasAnnotation): AgentCanvasPoint | null => {
  switch (annotation.kind) {
    case 'mark':
      return annotation.pos ? { x: annotation.pos.x + 12, y: annotation.pos.y + 12 } : null;
    case 'stroke': {
      const path = annotation.path;
      if (!path || path.length === 0) return null;
      const mid = path[Math.floor(path.length / 2)];
      return { x: mid.x, y: mid.y };
    }
    case 'line':
    case 'arrow':
      if (!annotation.start || !annotation.end) return null;
      return {
        x: (annotation.start.x + annotation.end.x) / 2,
        y: (annotation.start.y + annotation.end.y) / 2,
      };
    case 'box':
      if (!annotation.tl || !annotation.br) return null;
      return {
        x: Math.max(annotation.tl.x, annotation.br.x) + 12,
        y: Math.min(annotation.tl.y, annotation.br.y),
      };
    case 'circle':
      if (!annotation.pos || annotation.r == null) return null;
      return { x: annotation.pos.x + annotation.r + 12, y: annotation.pos.y - annotation.r };
    default:
      return null;
  }
};

const measureSurface = (surface: HTMLElement): Size => {
  const rect = surface.getBoundingClientRect();
  return {
    width: Math.max(1, Math.round(Math.max(rect.width, surface.scrollWidth, surface.clientWidth))),
    height: Math.max(1, Math.round(Math.max(rect.height, surface.scrollHeight, surface.clientHeight))),
  };
};

const drawAnnotation = (
  ctx: CanvasRenderingContext2D,
  annotation: AgentCanvasAnnotation,
) => {
  ctx.strokeStyle = annotation.color;
  ctx.fillStyle = annotation.color;
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (annotation.kind) {
    case 'stroke': {
      const path = annotation.path;
      if (!path || path.length === 0) return;
      if (path.length === 1) {
        ctx.beginPath();
        ctx.arc(path[0].x, path[0].y, LINE_WIDTH, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length - 1; i += 1) {
        const cur = path[i];
        const nxt = path[i + 1];
        ctx.quadraticCurveTo(cur.x, cur.y, (cur.x + nxt.x) / 2, (cur.y + nxt.y) / 2);
      }
      const last = path[path.length - 1];
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
      return;
    }

    case 'comment': {
      if (!annotation.pos) return;
      const { x, y } = annotation.pos;
      ctx.beginPath();
      ctx.arc(x, y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#111318';
      ctx.stroke();
      ctx.font = '700 11px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#111318';
      ctx.fillText(annotation.marker ?? '', x, y + 0.5);
      return;
    }

    case 'mark': {
      if (!annotation.pos) return;
      const { x, y } = annotation.pos;
      const r = 4;
      ctx.beginPath();
      ctx.moveTo(x - r, y - r); ctx.lineTo(x + r, y + r);
      ctx.moveTo(x + r, y - r); ctx.lineTo(x - r, y + r);
      ctx.stroke();
      return;
    }

    case 'line': {
      if (!annotation.start || !annotation.end) return;
      ctx.beginPath();
      ctx.moveTo(annotation.start.x, annotation.start.y);
      ctx.lineTo(annotation.end.x, annotation.end.y);
      ctx.stroke();
      return;
    }

    case 'arrow': {
      if (!annotation.start || !annotation.end) return;
      const { x: sx, y: sy } = annotation.start;
      const { x: ex, y: ey } = annotation.end;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      const len = Math.hypot(ex - sx, ey - sy);
      if (len < 1) return;
      const ux = (ex - sx) / len;
      const uy = (ey - sy) / len;
      const head = Math.max(8, ctx.lineWidth * 4);
      const angle = Math.PI / 6;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const lx = ex - head * (ux * cosA - uy * sinA);
      const ly = ey - head * (uy * cosA + ux * sinA);
      const rx = ex - head * (ux * cosA + uy * sinA);
      const ry = ey - head * (uy * cosA - ux * sinA);
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(ex, ey);
      ctx.lineTo(rx, ry);
      ctx.stroke();
      return;
    }

    case 'box': {
      if (!annotation.tl || !annotation.br) return;
      const x = Math.min(annotation.tl.x, annotation.br.x);
      const y = Math.min(annotation.tl.y, annotation.br.y);
      const w = Math.abs(annotation.br.x - annotation.tl.x);
      const h = Math.abs(annotation.br.y - annotation.tl.y);
      ctx.strokeRect(x, y, w, h);
      return;
    }

    case 'circle': {
      if (!annotation.pos || annotation.r == null) return;
      ctx.beginPath();
      ctx.arc(annotation.pos.x, annotation.pos.y, annotation.r, 0, Math.PI * 2);
      ctx.stroke();
      return;
    }

    case 'text': {
      if (!annotation.pos) return;
      const text = annotation.text ?? '';
      ctx.font = TEXT_FONT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const lines = text.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, annotation.pos!.x, annotation.pos!.y + i * TEXT_LINE_HEIGHT);
      });
      return;
    }
  }
};

const compositeScreenshotWithAnnotations = async (
  screenshot: EncodedImage,
  annotationSize: Size,
  annotations: AgentCanvasAnnotation[],
): Promise<EncodedImage> => {
  const baseImage = await loadImage(`data:${screenshot.mimeType};base64,${screenshot.base64}`);
  const width = baseImage.naturalWidth || baseImage.width;
  const height = baseImage.naturalHeight || baseImage.height;
  if (width <= 0 || height <= 0) {
    throw new Error('Agent Canvas screenshot had no image dimensions.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not compose Agent Canvas screenshot.');

  ctx.drawImage(baseImage, 0, 0, width, height);
  ctx.save();
  ctx.scale(width / annotationSize.width, height / annotationSize.height);
  for (const annotation of annotations) drawAnnotation(ctx, annotation);
  ctx.restore();

  const requestedMimeType = screenshot.mimeType === 'image/webp' ? 'image/webp' : 'image/png';
  const dataUrl = canvas.toDataURL(requestedMimeType, 1);
  return {
    mimeType: dataUrlMimeType(dataUrl),
    base64: dataUrlBase64(dataUrl),
  };
};

export const AgentCanvasOverlay = forwardRef<AgentCanvasOverlayHandle, AgentCanvasOverlayProps>((props, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<Draft | null>(null);
  const annotationsRef = useRef<AgentCanvasAnnotation[]>([]);

  const [tool, setTool] = useState<AgentCanvasTool | null>(
    props.initialTool === undefined ? 'cursor' : props.initialTool,
  );
  const handleToolClick = useCallback(
    (candidate: AgentCanvasTool) => {
      setTool((current) => (current === candidate ? null : candidate));
    },
    [],
  );
  const railSlot = useAgentCanvasToolbarSlot();
  const isMobile = useIsMobileDevice();
  const isPaperActive = usePaperThemeActive();
  const [color, setColor] = useState(PALETTE[0]);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const [visibleSize, setVisibleSize] = useState<Size>({ width: 0, height: 0 });
  const [panelHovering, setPanelHovering] = useState(false);
  const [annotations, setAnnotations] = useState<AgentCanvasAnnotation[]>([]);
  const [pointEditor, setPointEditor] = useState<PointEditorState | null>(null);
  /**
   * Anchor where a note editor should open if the user starts typing. Set
   * after a shape is committed via pointer-up; cleared on Escape, on the
   * first printable keystroke (which opens the editor with that char), or
   * when the user starts a new drawing.
   */
  const [pendingNoteAnchor, setPendingNoteAnchor] = useState<AgentCanvasPoint | null>(null);
  const pointEditorRef = useRef<HTMLTextAreaElement>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    annotationsRef.current = annotations;
  }, [annotations]);

  // Whenever the point editor opens (was null, now non-null), push the
  // caret to the end. autoFocus on a controlled textarea with a non-empty
  // initial value places the caret at position 0 in some browsers, which
  // makes subsequent typing land *before* the prefilled char.
  useEffect(() => {
    if (!pointEditor) return;
    const el = pointEditorRef.current;
    if (!el) return;
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [pointEditor !== null]);

  /**
   * While a note is "armed" after a shape commit, listen for a printable
   * keystroke at the document level and open the editor with that char as
   * the initial value. Escape (or any new drawing) cancels the armed
   * state. Ignores keystrokes when focus is in another input / textarea /
   * contenteditable so the user can type elsewhere without hijacking.
   */
  useEffect(() => {
    if (!pendingNoteAnchor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.key === 'Escape') {
        // The armed-note state is invisible to the user, so a single
        // Escape should both cancel it and deselect the active tool —
        // otherwise it takes two presses after every shape commit.
        setPendingNoteAnchor(null);
        setTool(null);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return;
      e.preventDefault();
      setPointEditor({ kind: 'comment', pos: pendingNoteAnchor, value: e.key });
      setPendingNoteAnchor(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [pendingNoteAnchor]);

  // Enter submits the canvas when there's something to send. The note
  // editor textarea and the comment textarea (both <textarea>s) own
  // Enter for their own commit behavior — the input/textarea guard
  // below keeps this handler from stealing it. handleSend is captured
  // via a ref so the listener always invokes the latest closure
  // without re-binding on every render.
  const handleSendRef = useRef<() => Promise<void>>(async () => {});
  const drawAllRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!props.active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
        // Don't steal Enter from focused buttons, links, or other
        // interactive controls — Enter activates them.
        if (
          tag === 'BUTTON' ||
          tag === 'A' ||
          tag === 'SELECT' ||
          tag === 'SUMMARY' ||
          tag === 'OPTION' ||
          target.getAttribute('role') === 'button' ||
          target.getAttribute('role') === 'link' ||
          target.getAttribute('role') === 'menuitem' ||
          target.getAttribute('role') === 'tab'
        )
          return;
      }
      const hasPendingCanvasComment = (pointEditor?.value.trim().length ?? 0) > 0;
      const canSend = annotationsRef.current.length > 0 || hasPendingCanvasComment;
      if (!canSend || sending) return;
      e.preventDefault();
      void handleSendRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [props.active, pointEditor, sending]);

  // Escape clears the active tool selection, returning the canvas to its
  // passive (cursor) state. Skipped while a note editor or pending-note
  // anchor is open — those have their own Escape handling and should
  // swallow it first.
  useEffect(() => {
    if (!props.active) return;
    if (!tool || tool === 'cursor') return;
    if (pointEditor || pendingNoteAnchor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      e.preventDefault();
      // Mirror the comment-cancel flow: if the user is mid-drag, the
      // first Escape just drops the draft (and clears its preview) and
      // leaves the draw tool selected so they can retry. Only a second
      // Escape, with no draft in flight, clears the tool selection.
      if (draftRef.current !== null) {
        draftRef.current = null;
        drawAllRef.current();
        return;
      }
      setTool(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [props.active, tool, pointEditor, pendingNoteAnchor]);

  useEffect(() => {
    if (!props.active) {
      draftRef.current = null;
      setPointEditor(null);
      setPendingNoteAnchor(null);
      setCommentOpen(false);
      setColorPickerOpen(false);
      setPanelHovering(false);
    }
  }, [props.active]);

  useLayoutEffect(() => {
    if (!props.active) return;
    // Walk up from this component's own root div — local refs are
    // guaranteed set when the component's layoutEffect runs, unlike
    // `props.containerRef.current`, which we observed to be null at
    // this point in production builds.
    const root = rootRef.current;
    if (!root) return;
    const themeRoot = root.closest<HTMLElement>('.a-theme-root') ?? props.containerRef.current;
    if (!themeRoot) return;

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const attach = (surface: HTMLElement) => {
      const updateSize = () => {
        const rect = surface.getBoundingClientRect();
        setSize({
          width: Math.max(1, Math.round(Math.max(rect.width, surface.scrollWidth))),
          height: Math.max(1, Math.round(Math.max(rect.height, surface.scrollHeight))),
        });
      };
      updateSize();
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(surface);
    };

    const surface = themeRoot.querySelector<HTMLElement>('.a-surface');
    if (surface) {
      attach(surface);
    } else {
      mutationObserver = new MutationObserver(() => {
        const found = themeRoot.querySelector<HTMLElement>('.a-surface');
        if (found) {
          mutationObserver?.disconnect();
          mutationObserver = null;
          attach(found);
        }
      });
      mutationObserver.observe(themeRoot, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [props.active, props.containerRef]);

  useLayoutEffect(() => {
    if (!props.active) return;
    const container = props.containerRef.current;
    if (!container) return;

    const updateVisibleSize = () => {
      setVisibleSize({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    };

    updateVisibleSize();
    const observer = new ResizeObserver(updateVisibleSize);
    observer.observe(container);
    window.addEventListener('resize', updateVisibleSize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateVisibleSize);
    };
  }, [props.active, props.containerRef]);

  useEffect(() => {
    if (!props.active) return;
    const container = props.containerRef.current;
    if (!container) return;

    const handleEnter = () => setPanelHovering(true);
    const handleLeave = () => setPanelHovering(false);
    container.addEventListener('pointerenter', handleEnter);
    container.addEventListener('pointerleave', handleLeave);
    return () => {
      container.removeEventListener('pointerenter', handleEnter);
      container.removeEventListener('pointerleave', handleLeave);
    };
  }, [props.active, props.containerRef]);

  const drawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const annotation of annotationsRef.current) drawAnnotation(ctx, annotation);
    const draft = draftRef.current;
    if (!draft) return;
    if (draft.tool === 'stroke') {
      drawAnnotation(ctx, { kind: 'stroke', color, path: draft.path });
      return;
    }
    if (draft.tool === 'line' || draft.tool === 'arrow') {
      drawAnnotation(ctx, { kind: draft.tool, color, start: draft.start, end: draft.end });
      return;
    }
    if (draft.tool === 'box') {
      drawAnnotation(ctx, { kind: 'box', color, tl: draft.start, br: draft.end });
      return;
    }
    if (draft.tool === 'circle') {
      const r = Math.hypot(draft.end.x - draft.start.x, draft.end.y - draft.start.y);
      drawAnnotation(ctx, { kind: 'circle', color, pos: draft.start, r });
    }
  }, [color]);

  useEffect(() => {
    drawAllRef.current = drawAll;
  }, [drawAll]);

  useEffect(() => {
    drawAll();
  }, [drawAll, annotations, size]);

  const pointFromEvent = (event: ReactPointerEvent<HTMLCanvasElement>): AgentCanvasPoint => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: Math.round((event.clientX - rect.left) * scaleX),
      y: Math.round((event.clientY - rect.top) * scaleY),
    };
  };

  const metadataForPoint = useCallback((point: AgentCanvasPoint) => {
    const surface = props.containerRef.current?.querySelector<HTMLElement>('.a-surface');
    return surface ? findAgentCanvasAnnotationMetadataAtPoint(surface, point) : undefined;
  }, [props.containerRef]);

  const commitPointEditor = useCallback((editor = pointEditor) => {
    if (!editor) return;
    setPointEditor(null);
    const text = editor.value.trim();
    if (!text) return;
    const current = annotationsRef.current;
    const annotation: AgentCanvasAnnotation =
      editor.kind === 'comment'
        ? {
            kind: 'comment',
            color,
            marker: nextCommentMarker(current),
            pos: editor.pos,
            text,
            metadata: metadataForPoint(editor.pos),
          }
        : {
            kind: 'text',
            color,
            pos: editor.pos,
            text,
            metadata: metadataForPoint(editor.pos),
          };
    const next = [...current, annotation];
    annotationsRef.current = next;
    setAnnotations(next);
  }, [color, pointEditor, metadataForPoint]);

  const clearCanvas = useCallback(() => {
    draftRef.current = null;
    annotationsRef.current = [];
    setPointEditor(null);
    setPendingNoteAnchor(null);
    setAnnotations([]);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    if (!tool) return;
    const point = pointFromEvent(event);
    if (!tool || tool === 'cursor') return;

    // Auto-commit any pending note editor before starting a new action.
    // No-op when no editor is open. Skipped for comment/text because those
    // tools deliberately replace the editor at the new click point.
    if (tool !== 'comment' && tool !== 'text') {
      commitPointEditor();
    }
    // Starting any new action also discards an armed-but-not-typed note.
    setPendingNoteAnchor(null);

    if (tool === 'comment' || tool === 'text') {
      commitPointEditor();
      setPointEditor({ kind: tool, pos: point, value: '' });
      event.preventDefault();
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === 'stroke') {
      draftRef.current = { tool: 'stroke', path: [point] };
    } else {
      // line / arrow / box / circle — two-point drag
      draftRef.current = { tool, start: point, end: point };
    }
    drawAll();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const point = pointFromEvent(event);
    const draft = draftRef.current;
    if (!draft) return;
    if (draft.tool === 'stroke') {
      const previous = draft.path[draft.path.length - 1];
      const dx = point.x - previous.x;
      const dy = point.y - previous.y;
      if (dx * dx + dy * dy < 16) return;
      draft.path.push(point);
    } else {
      draft.end = point;
    }
    drawAll();
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const draft = draftRef.current;
    draftRef.current = null;
    if (!draft) return;
    const end = pointFromEvent(event);

    let committed: AgentCanvasAnnotation | null = null;
    if (draft.tool === 'stroke') {
      const last = draft.path[draft.path.length - 1];
      if (last.x !== end.x || last.y !== end.y) draft.path.push(end);
      if (draft.path.length > 1) {
        committed = { kind: 'stroke', color, path: draft.path };
      }
    } else if (draft.tool === 'line' || draft.tool === 'arrow') {
      if (end.x !== draft.start.x || end.y !== draft.start.y) {
        committed = { kind: draft.tool, color, start: draft.start, end };
      }
    } else if (draft.tool === 'box') {
      if (end.x !== draft.start.x || end.y !== draft.start.y) {
        committed = { kind: 'box', color, tl: draft.start, br: end };
      }
    } else if (draft.tool === 'circle') {
      const r = Math.hypot(end.x - draft.start.x, end.y - draft.start.y);
      if (r >= 1) {
        committed = { kind: 'circle', color, pos: draft.start, r };
      }
    }

    if (committed !== null) {
      const annotation = committed;
      setAnnotations((prev) => {
        const next = [...prev, annotation];
        annotationsRef.current = next;
        return next;
      });
      // Arm the note editor at the shape's anchor. The editor only opens
      // once the user actually types — see the keydown effect above.
      // Clamp into the visible canvas so editors don't open off-screen
      // when an annotation extends past the artifact viewport.
      const anchor = shapeNoteAnchor(annotation);
      if (anchor) {
        const EDITOR_W = 256;
        const EDITOR_H = 80;
        const MARGIN = 8;
        const maxX = Math.max(MARGIN, visibleSize.width - EDITOR_W - MARGIN);
        const maxY = Math.max(MARGIN, visibleSize.height - EDITOR_H - MARGIN);
        setPendingNoteAnchor({
          x: Math.min(maxX, Math.max(MARGIN, anchor.x)),
          y: Math.min(maxY, Math.max(MARGIN, anchor.y)),
        });
      }
    }
    drawAll();
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* ignore */ }
  };

  const makeAnnotatedScreenshot = async () => {
    const surface = props.containerRef.current?.querySelector<HTMLElement>('.a-surface');
    if (!surface) throw new Error('No panel surface to capture.');

    const captureSize = measureSurface(surface);
    const snapshot = await exportSurfaceSnapshot(
      surface,
      'png',
      undefined,
      undefined,
      undefined,
      { imageMimeType: 'image/webp' },
    );
    return compositeScreenshotWithAnnotations(snapshot, captureSize, annotationsRef.current);
  };

  const handleSend = async () => {
    const hasPendingCanvasComment = (pointEditor?.value.trim().length ?? 0) > 0;
    const canSend = annotationsRef.current.length > 0 || hasPendingCanvasComment;
    if (sending || !canSend) return;
    commitPointEditor();
    setSending(true);
    try {
      const image = await makeAnnotatedScreenshot();
      const viewport: AgentCanvasViewport = {
        width: size.width,
        height: size.height,
        scrollY: props.containerRef.current?.scrollTop ?? 0,
      };
      const sent = props.onSubmit({
        type: 'agentCanvasSubmission',
        imageMimeType: image.mimeType,
        imageBase64: image.base64,
        annotations: annotationsRef.current,
        viewport,
        comment: comment.trim() || null,
      });
      if (sent) {
        clearCanvas();
        setComment('');
        setCommentOpen(false);
        props.onSubmitted?.();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(`Could not send Agent Canvas: ${message}`);
    } finally {
      setSending(false);
    }
  };

  // Keep the keydown listener's send-ref pointed at the latest closure.
  handleSendRef.current = handleSend;

  useImperativeHandle(ref, () => ({
    collectContext: async () => {
      const hasPendingCanvasComment = (pointEditor?.value.trim().length ?? 0) > 0;
      const canSend = annotationsRef.current.length > 0 || hasPendingCanvasComment;
      if (!canSend) return null;
      commitPointEditor();
      const image = await makeAnnotatedScreenshot();
      return {
        imageMimeType: image.mimeType,
        imageBase64: image.base64,
        annotations: annotationsRef.current,
        viewport: {
          width: size.width,
          height: size.height,
          scrollY: props.containerRef.current?.scrollTop ?? 0,
        },
        comment: null,
      };
    },
    clear: () => {
      clearCanvas();
      setComment('');
      setCommentOpen(false);
    },
  }), [clearCanvas, pointEditor, commitPointEditor, makeAnnotatedScreenshot, props.containerRef, size.height, size.width]);

  if (!props.active) return null;

  const hasPendingCanvasComment = (pointEditor?.value.trim().length ?? 0) > 0;
  const canSend = annotations.length > 0 || hasPendingCanvasComment;
  const toolbarExpanded = panelHovering || colorPickerOpen || commentOpen;
  const paperTools = ALL_TOOLS.filter((t) => t === 'stroke' || t === 'comment');
  const showPaperMobileToolbar = props.initialTool === null && isMobile && isPaperActive;

  const paperRailToolbar = railSlot && !showPaperMobileToolbar ? (
    <PaperRailCanvasToolbar
      tool={tool}
      color={color}
      tools={paperTools}
      colorPickerOpen={colorPickerOpen}
      setColorPickerOpen={setColorPickerOpen}
      onSelectTool={handleToolClick}
      onSelectColor={(c) => {
        setColor(c);
        setColorPickerOpen(false);
      }}
      onClear={clearCanvas}
      onSend={handleSend}
      canSend={canSend}
      sending={sending}
    />
  ) : null;
  const paperMobileToolbar = showPaperMobileToolbar ? (
    <PaperMobileCanvasToolbar
      tool={tool}
      color={color}
      tools={paperTools}
      colorPickerOpen={colorPickerOpen}
      setColorPickerOpen={setColorPickerOpen}
      onSelectTool={handleToolClick}
      onSelectColor={(c) => {
        setColor(c);
        setColorPickerOpen(false);
      }}
      onClear={clearCanvas}
      onSend={handleSend}
      canSend={canSend}
      sending={sending}
    />
  ) : null;

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute left-0 top-0 z-50"
      style={{ width: size.width, height: size.height }}
    >
      {railSlot && paperRailToolbar && createPortal(paperRailToolbar, railSlot)}
      {paperMobileToolbar}
      {!railSlot && !showPaperMobileToolbar && (
      <div
        className="pointer-events-none sticky left-0 top-0 z-50"
        style={{ width: visibleSize.width, height: visibleSize.height }}
      >
        <div
          className={cn(
            'pointer-events-auto absolute z-40 flex -translate-y-1/2 flex-col items-center rounded-full border border-white/[0.08] bg-[#181818]/90 shadow-[0_18px_48px_rgba(0,0,0,0.45)] backdrop-blur-xl transition-[width,height,padding,opacity] duration-150 ease-out',
            toolbarExpanded
              ? 'w-14 gap-2 px-2 py-3'
              : 'h-10 w-1.5 justify-center border-[#323232] bg-[#323232] px-0 py-0 opacity-70 shadow-none hover:opacity-100',
          )}
          style={{ right: 16, top: '50%' }}
          onPointerEnter={() => setPanelHovering(true)}
        >
        {!toolbarExpanded ? (
          <div className="h-full w-full rounded-full bg-[#323232]" />
        ) : (
          <>
        {ALL_TOOLS.map((candidate) => (
          <Tooltip key={candidate} delayDuration={3000}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={TOOL_TIPS[candidate].title}
                aria-pressed={tool === candidate}
                onClick={() => setTool(candidate)}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200',
                  tool === candidate && 'bg-white/[0.10] text-[#a78bfa] shadow-[inset_0_0_0_1px_rgba(167,139,250,0.22)]',
                )}
              >
                {TOOL_ICON[candidate]}
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={12}>
              <ToolTipBody {...TOOL_TIPS[candidate]} />
            </TooltipContent>
          </Tooltip>
        ))}
        <Tooltip delayDuration={3000}>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="clear annotations"
              onClick={clearCanvas}
              className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
            >
              <Trash className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={12}>
            <ToolTipBody title="Clear marks" description="Remove the current Canvas notes and start fresh." />
          </TooltipContent>
        </Tooltip>
        <div className="my-0.5 h-px w-8 bg-white/[0.10]" />
        <div className="relative">
          <Tooltip delayDuration={3000}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="annotation color"
                aria-haspopup="true"
                aria-expanded={colorPickerOpen}
                onClick={() => setColorPickerOpen((value) => !value)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
              >
                <span
                  className="h-4 w-4 rounded-full border border-white/80 shadow-[0_0_0_2px_rgba(255,255,255,0.08)]"
                  style={{ background: color }}
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={12}>
              <ToolTipBody title="Color" description="Use color to group related feedback or separate alternatives." />
            </TooltipContent>
          </Tooltip>
          {colorPickerOpen && (
            <div className="absolute right-[calc(100%+10px)] top-1/2 z-50 flex -translate-y-1/2 flex-col items-center gap-1 rounded-full border border-white/[0.10] bg-[#181818]/95 p-2 shadow-xl backdrop-blur">
              {PALETTE.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  aria-label={`color ${candidate}`}
                  aria-pressed={candidate === color}
                  onClick={() => {
                    setColor(candidate);
                    setColorPickerOpen(false);
                  }}
                  className={cn(
                    'h-5 w-5 rounded-full border transition-transform hover:scale-110',
                    candidate === color ? 'border-white' : 'border-white/20',
                  )}
                  style={{ background: candidate }}
                />
              ))}
            </div>
          )}
        </div>
        <Tooltip delayDuration={3000}>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={!canSend}
              onClick={handleSend}
              aria-label="send Agent Canvas to agent"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-100 text-zinc-900 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
             >
              <ArrowUp className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={12}>
            <ToolTipBody title="Send now" description="Ask the agent about these marks without typing a chat message." />
          </TooltipContent>
        </Tooltip>
        {commentOpen && (
          <div className="absolute right-[calc(100%+10px)] bottom-2 w-72 rounded-2xl border border-white/[0.10] bg-[#181818]/95 p-3 shadow-xl backdrop-blur">
            <label className="mb-1 block text-[11px] text-zinc-400">
              Optional high-level comments
            </label>
            <textarea
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
              placeholder="What should the agent focus on?"
              className="h-24 w-full resize-none rounded border border-white/[0.10] bg-black/30 p-2 text-xs text-zinc-100 outline-none placeholder:text-zinc-600"
            />
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCommentOpen(false)}
                className="h-7 rounded px-2 text-[11px] text-zinc-400 hover:bg-white/[0.06] hover:text-zinc-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={sending || !canSend}
                onClick={handleSend}
                className="h-7 rounded bg-[#a78bfa] px-2 text-[11px] font-medium text-white hover:bg-[#b89efb] disabled:cursor-not-allowed disabled:bg-[#4b3b63] disabled:text-zinc-400 disabled:opacity-50 disabled:hover:bg-[#4b3b63]"
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )}
          </>
        )}
        </div>
      </div>
      )}
      <canvas
        ref={canvasRef}
        width={size.width}
        height={size.height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onContextMenu={(event) => event.preventDefault()}
        className={cn(
          'absolute inset-0 z-10 block touch-none select-none',
          (!tool || tool === 'cursor') ? 'pointer-events-none' : 'pointer-events-auto',
        )}
        style={{
          cursor: (!tool || tool === 'cursor') ? 'default' : tool === 'comment' ? 'copy' : 'crosshair',
        }}
      />
      {pointEditor && (
        <div
          className="pointer-events-auto absolute z-20 flex w-64 gap-1 rounded-lg border bg-[var(--surface)] p-1 shadow-xl"
          style={{
            left: pointEditor.pos.x,
            top: pointEditor.pos.y,
            borderColor: color,
          }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <textarea
            ref={pointEditorRef}
            autoFocus
            aria-label="Agent Canvas point comment"
            value={pointEditor.value}
            onChange={(event) => setPointEditor({ ...pointEditor, value: event.currentTarget.value })}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setPointEditor(null);
              } else if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                commitPointEditor();
              }
            }}
            placeholder="Comment on this point..."
            className="h-20 min-w-0 flex-1 resize-none rounded border border-[var(--hair)] bg-[var(--surface-2)] p-2 text-xs text-[var(--fg-2)] outline-none placeholder:text-[var(--fg-4)]"
          />
          <button
            type="button"
            aria-label="save point comment"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => commitPointEditor()}
            className={cn(
              PAPER_ICON_CONTROL_COMPACT_CLASS,
              'shrink-0',
            )}
          >
            <Check className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
          </button>
        </div>
      )}
    </div>
  );
});

/* ─── Paper-rail canvas toolbar: vertical stack docked into PaperArtifactRail ───
 * Purely presentational; state and handlers live in AgentCanvasOverlay.
 */
interface PaperRailCanvasToolbarProps {
  tool: AgentCanvasTool | null;
  color: string;
  tools: AgentCanvasTool[];
  colorPickerOpen: boolean;
  setColorPickerOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
  onSelectTool: (t: AgentCanvasTool) => void;
  onSelectColor: (c: string) => void;
  onClear: () => void;
  onSend: () => void;
  canSend: boolean;
  sending: boolean;
}

const RAIL_BTN_BASE_CLASS = cn(
  PAPER_ICON_CONTROL_CLASS,
);

const RAIL_BTN_ACTIVE_CLASS = cn(
  'bg-[color-mix(in_srgb,var(--surface)_86%,var(--periwinkle)_14%)] text-[var(--periwinkle)]',
  'hover:bg-[color-mix(in_srgb,var(--surface)_86%,var(--periwinkle)_14%)] hover:text-[var(--periwinkle)]',
);

const RAIL_SUBMIT_BTN_CLASS = cn(
  'disabled:bg-[var(--surface)] disabled:text-[var(--fg-3)]',
  'disabled:border disabled:border-solid disabled:border-[var(--hair)] disabled:opacity-100',
  'disabled:hover:bg-[var(--surface)]',
);

const MOBILE_FLOATING_PAPER_BTN_CLASS = cn(
  PAPER_ICON_CONTROL_CLASS,
  'h-10 w-10 border border-solid border-[var(--hair)] bg-[var(--surface)]',
  'text-[var(--fg-3)] shadow-[0_8px_24px_rgba(0,0,0,0.14)]',
  'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)]',
);

const MOBILE_LAUNCHER_BTN_CLASS = cn(
  'pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full',
  'border border-solid border-[var(--hair)] bg-[var(--surface)] text-[var(--fg-3)]',
  'shadow-[0_16px_42px_rgba(0,0,0,0.26)]',
  'cursor-pointer transition-[background-color,color,transform] duration-150',
  'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)] active:scale-95',
);

function PaperRailCanvasToolbar(props: PaperRailCanvasToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Agent canvas tools"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: '10px 0',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {props.tools.map((candidate) => {
        const active = props.tool === candidate;
        return (
          <Tooltip key={candidate} delayDuration={3000}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={candidate === 'stroke' ? 'freeform line' : candidate === 'comment' ? 'point comment' : candidate}
                aria-pressed={active}
                onClick={() => props.onSelectTool(candidate)}
                className={cn(
                  RAIL_BTN_BASE_CLASS,
                  active && RAIL_BTN_ACTIVE_CLASS,
                )}
              >
                {TOOL_ICON[candidate]}
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={20} className={PAPER_TOOLTIP_CLS}>
              <PaperToolTipBody {...TOOL_TIPS[candidate]} />
            </TooltipContent>
          </Tooltip>
        );
      })}

      {/* clear — sits directly under the tools so it reads as part of
          "what to do with the marks" rather than next to the send button. */}
      <Tooltip delayDuration={3000}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="clear annotations"
            onClick={props.onClear}
            className={RAIL_BTN_BASE_CLASS}
          >
            <Trash className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={20} className={PAPER_TOOLTIP_CLS}>
          <PaperToolTipBody title="Clear marks" description="Remove the current Canvas notes and start fresh." />
        </TooltipContent>
      </Tooltip>

      {/* divider */}
      <div style={{ width: 24, height: 1, background: 'var(--hair)', margin: '6px 0' }} />

      {/* color picker — single swatch with portaled popover so the
          palette isn't clipped by the rail's overflow:hidden chrome. */}
      <PaperRailColorPicker
        color={props.color}
        open={props.colorPickerOpen}
        setOpen={props.setColorPickerOpen}
        onSelectColor={props.onSelectColor}
      />

      {/* spacer pushes the send button to the bottom of the pill. */}
      <div style={{ flex: 1, minHeight: 8 }} />

      {/* send — filled control so it reads as the affordance to dispatch
          the canvas. */}
      <Tooltip delayDuration={3000}>
        <TooltipTrigger asChild>
          <PaperChatSubmitButton
            type="button"
            aria-label="send to agent"
            disabled={!props.canSend || props.sending}
            onClick={props.onSend}
            isSending={props.sending}
            className={RAIL_SUBMIT_BTN_CLASS}
          />
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={20} className={PAPER_TOOLTIP_CLS}>
          <PaperToolTipBody title="Send now" description="Ask the agent about these marks without typing a chat message." />
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function PaperMobileCanvasToolbar(props: PaperRailCanvasToolbarProps) {
  const [open, setOpen] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const pendingTapRef = useRef<number | null>(null);
  // Seed with the same tool the launcher shows by default (Pen → stroke), so a
  // double-tap on the initial widget activates stroke instead of falling
  // through to single-tap.
  const lastToolRef = useRef<AgentCanvasTool | null>('stroke');
  const launcherTool = props.tool && props.tool !== 'cursor' ? props.tool : null;
  const launcherIcon = launcherTool ? TOOL_ICON[launcherTool] : <Pen {...PAPER_CANVAS_ICON_PROPS} />;

  // Remember the most recent real tool so a double-tap can re-activate it
  // after the user has cleared the selection.
  useEffect(() => {
    if (launcherTool) lastToolRef.current = launcherTool;
  }, [launcherTool]);

  useEffect(() => () => {
    if (pendingTapRef.current !== null) window.clearTimeout(pendingTapRef.current);
  }, []);

  const closeColorPicker = () => props.setColorPickerOpen(false);
  const DOUBLE_TAP_MS = 260;
  const handleLauncherClick = () => {
    // Defer the single-tap (open/close column) so a fast second tap can
    // pre-empt it as a double-tap without the column flashing open first.
    if (pendingTapRef.current !== null) {
      // Second tap inside the window → double-tap: toggle the active or
      // last-used tool. onSelectTool is a toggle on the same tool.
      window.clearTimeout(pendingTapRef.current);
      pendingTapRef.current = null;
      const target = launcherTool ?? lastToolRef.current;
      if (target) {
        props.onSelectTool(target);
        setOpen(false);
        closeColorPicker();
        return;
      }
      // No tool to toggle → fall through to single-tap intent.
    }
    pendingTapRef.current = window.setTimeout(() => {
      pendingTapRef.current = null;
      setOpen((prev) => {
        const next = !prev;
        if (!next) closeColorPicker();
        return next;
      });
    }, DOUBLE_TAP_MS);
  };

  const handleSelectTool = (candidate: AgentCanvasTool) => {
    props.onSelectTool(candidate);
    closeColorPicker();
    setOpen(false);
  };

  const handleSend = () => {
    props.onSend();
    closeColorPicker();
    setOpen(false);
  };
  const hasPendingInput = props.canSend || props.sending;

  // Collapse the column on taps outside the FAB. The color picker portals into
  // document.body (role="menu"), so it sits outside fabRef — exempt it so
  // tapping a swatch doesn't dismiss the launcher.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (fabRef.current?.contains(target)) return;
      if (target.closest('[role="menu"]')) return;
      setOpen(false);
      closeColorPicker();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Position relative to the viewport rather than the artifact scroll
  // container. The scroll container's visible width/height isn't known until
  // after layout, so anchoring to a measured size races with the first paint
  // when the panel remounts — the FAB can briefly land in a 0×0 box and
  // render off-screen. The mobile artifact panel is fullscreen, with only the
  // tab bar below it, so the viewport's bottom-right (minus the tab bar) is
  // the same target.
  return (
    <div
      ref={fabRef}
      className="pointer-events-none fixed z-50"
      style={{
        right: 'var(--paper-mobile-canvas-fab-inset)',
        bottom: 'calc(var(--paper-mobile-tab-bar-height, 0px) + var(--paper-mobile-canvas-fab-inset))',
      }}
    >
        {(open || hasPendingInput) && (
          <div
            role="toolbar"
            aria-label="Agent canvas tools"
            className="pointer-events-none absolute bottom-[68px] right-[8px] flex flex-col items-center gap-[10px]"
          >
            {open && (
              <>
                <PaperRailColorPicker
                  color={props.color}
                  open={props.colorPickerOpen}
                  setOpen={props.setColorPickerOpen}
                  onSelectColor={props.onSelectColor}
                  buttonClassName={cn(MOBILE_FLOATING_PAPER_BTN_CLASS, 'pointer-events-auto')}
                  tooltipSideOffset={16}
                />

                <Tooltip delayDuration={3000}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="clear annotations"
                      onClick={props.onClear}
                      className={cn(MOBILE_FLOATING_PAPER_BTN_CLASS, 'pointer-events-auto')}
                    >
                      <Trash className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" sideOffset={16} className={PAPER_TOOLTIP_CLS}>
                    <PaperToolTipBody title="Clear marks" description="Remove the current Canvas notes and start fresh." />
                  </TooltipContent>
                </Tooltip>

                {/* Reverse so tool order reads top→down above the launcher. */}
                {[...props.tools].reverse().map((candidate) => {
                  const active = props.tool === candidate;
                  return (
                    <Tooltip key={candidate} delayDuration={3000}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={candidate === 'stroke' ? 'freeform line' : candidate === 'comment' ? 'point comment' : candidate}
                          aria-pressed={active}
                          onClick={() => handleSelectTool(candidate)}
                          className={cn(
                            MOBILE_FLOATING_PAPER_BTN_CLASS,
                            'pointer-events-auto',
                            active && RAIL_BTN_ACTIVE_CLASS,
                          )}
                        >
                          {TOOL_ICON[candidate]}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" sideOffset={16} className={PAPER_TOOLTIP_CLS}>
                        <PaperToolTipBody {...TOOL_TIPS[candidate]} />
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </>
            )}

            <PaperChatSubmitButton
              type="button"
              aria-label="send to agent"
              disabled={!props.canSend || props.sending}
              onClick={handleSend}
              isSending={props.sending}
              className={cn(
                RAIL_SUBMIT_BTN_CLASS,
                'pointer-events-auto shadow-[0_8px_24px_rgba(0,0,0,0.14)]',
              )}
            />
          </div>
        )}

        <button
          type="button"
          aria-label={open ? 'Close markup tools' : launcherTool ? `${TOOL_TIPS[launcherTool].title} selected` : 'Open markup tools'}
          aria-expanded={open}
          onPointerDown={(e) => {
            // Drive the tap via pointerdown so a fast double-tap doesn't get
            // its second click suppressed by the browser (double-tap-to-zoom
            // heuristics, 300ms click delay).
            e.preventDefault();
            handleLauncherClick();
          }}
          style={{ touchAction: 'manipulation' }}
          className={cn(
            MOBILE_LAUNCHER_BTN_CLASS,
            launcherTool && RAIL_BTN_ACTIVE_CLASS,
          )}
        >
          {launcherIcon}
        </button>
    </div>
  );
}

interface PaperRailColorPickerProps {
  color: string;
  open: boolean;
  setOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
  onSelectColor: (c: string) => void;
  buttonClassName?: string;
  tooltipSideOffset?: number;
}

/** The swatch button + a portaled popover. The popover renders into
 *  `document.body` because the surrounding rail uses `overflow: hidden`
 *  to clip its chrome — anything `position: absolute` inside it is invisible
 *  the moment it crosses the rail border. */
const PaperRailColorPicker = (props: PaperRailColorPickerProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!props.open) {
      setPos(null);
      return;
    }
    const computePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Anchor the popover to the LEFT of the swatch, vertically centered
      // on it. The popover measures itself once mounted; until then we
      // approximate to keep first-paint stable.
      const popoverWidth = popoverRef.current?.offsetWidth ?? 96;
      const popoverHeight = popoverRef.current?.offsetHeight ?? 96;
      setPos({
        top: rect.top + rect.height / 2 - popoverHeight / 2,
        left: rect.left - popoverWidth - 8,
      });
    };
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
  }, [props.open]);

  useEffect(() => {
    if (!props.open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      props.setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [props.open, props.setOpen]);

  return (
    <>
      <Tooltip delayDuration={3000}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="annotation color"
            aria-haspopup="true"
            aria-expanded={props.open}
            onClick={() => props.setOpen((v) => !v)}
            className={props.buttonClassName ?? RAIL_BTN_BASE_CLASS}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: PAPER_CONTROL_RADIUS_VALUE,
                border: '1px solid rgba(0,0,0,0.2)',
                background: props.color,
                display: 'block',
              }}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={props.tooltipSideOffset ?? 20} className={PAPER_TOOLTIP_CLS}>
          <PaperToolTipBody title="Color" description="Use color to group related feedback or separate alternatives." />
        </TooltipContent>
      </Tooltip>
      {props.open && createPortal(
        <div
          ref={popoverRef}
          role="menu"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 22px)',
            gap: 6,
            padding: 8,
            background: 'var(--surface)',
            border: '1px solid var(--hair)',
            borderRadius: PAPER_CHROME_RADIUS_VALUE,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 80,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          {PALETTE.map((c) => {
            const active = c === props.color;
            return (
              <button
                key={c}
                type="button"
                aria-label={`color ${c}`}
                aria-pressed={active}
                onClick={() => props.onSelectColor(c)}
                style={{
                  width: 22,
                  height: 22,
                  padding: 0,
                  border: active ? '2px solid var(--fg)' : '1px solid rgba(0,0,0,0.2)',
                  borderRadius: PAPER_CONTROL_RADIUS_VALUE,
                  background: c,
                  cursor: 'pointer',
                }}
              />
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
};
